// What the signed-in role may see and do in the console (WEB_PLAN.md §3.12 role matrix, §5.0 route map, §3.1 rep
// console). The server (`sync_items_before_write`, M1:460-627, and the membership RPCs) is the authority and the UI
// still handles `forbidden`; these flags only hide what would be refused, plus the console's own product choices
// (reps get a read-only console; routes and day logs are never edited from the web).
//
// Gates mirror the iOS ones: isAdmin = role == admin (`UserRole.canConfigureCompany`), isManager = role != rep
// (`UserRole.canManageTeam`, `AppStore.isManager`).

import { useMemo } from 'react';
import { useSession } from '@/auth/AuthProvider';
import type { Collection } from '@/contracts/collections';
import type { Role } from '@/contracts/wire';
import type { SessionCtx } from './types';

/**
 * Server write access to a collection for a role:
 * - `write`: insert, update and tombstone any item;
 * - `own`: only items tied to the caller (rep records owned by them or unowned, own activities/comms/attachments/
 *   enrollments or ones on visible records, own routes/day logs/documents; managers' and reps' own `users` profile);
 * - `read`: visible but not writable;
 * - `none`: not even readable (`integrations` for reps).
 */
export type CollectionAccess = 'write' | 'own' | 'read' | 'none';

const ADMIN_ONLY: ReadonlySet<Collection> = new Set<Collection>([
  'settings',
  'pipelines',
  'visitResults',
  'customFields',
  'leadSources',
  'badges',
  'links',
  'integrations',
  'teams',
]);

const MANAGERS_WRITE: ReadonlySet<Collection> = new Set<Collection>(['territories', 'autoplays', 'templates', 'goals']);

/** §3.12 row for one collection: what the server lets `role` write. */
export function collectionAccess(role: Role, collection: Collection): CollectionAccess {
  if (ADMIN_ONLY.has(collection)) {
    if (role === 'admin') return 'write';
    // Reps cannot read integrations (no rows in their feed); managers read everything in this group.
    return role === 'rep' && collection === 'integrations' ? 'none' : 'read';
  }
  if (MANAGERS_WRITE.has(collection)) return role === 'rep' ? 'read' : 'write';
  if (collection === 'users') return role === 'admin' ? 'write' : 'own';
  // records, activities, comms, attachments, enrollments, routes, dayLogs, documents.
  return role === 'rep' ? 'own' : 'write';
}

/** Console areas, one per left-rail entry plus the account page and the leaderboard (§5.0). */
export const CONSOLE_ROUTES = [
  'dashboard',
  'stores',
  'followUps',
  'map',
  'pipeline',
  'territories',
  'team',
  'documents',
  'reports',
  'leaderboard',
  'settings',
  'account',
] as const;

export type ConsoleRoute = (typeof CONSOLE_ROUTES)[number];

/** First path segment → console area. */
const ROUTE_BY_SEGMENT: Readonly<Record<string, ConsoleRoute>> = {
  '': 'dashboard',
  stores: 'stores',
  'follow-ups': 'followUps',
  map: 'map',
  pipeline: 'pipeline',
  territories: 'territories',
  team: 'team',
  documents: 'documents',
  reports: 'reports',
  leaderboard: 'leaderboard',
  settings: 'settings',
  account: 'account',
};

/** The console area a pathname belongs to ("/stores/ABC" → stores), or null for paths outside the console. */
export function consoleRouteFor(pathname: string): ConsoleRoute | null {
  const segment = pathname.replace(/^\/+/, '').split(/[/?#]/)[0] ?? '';
  return ROUTE_BY_SEGMENT[segment.toLowerCase()] ?? null;
}

export interface Permissions {
  role: Role;
  isAdmin: boolean;
  /** Admin or manager (iOS `isManager`). */
  isManager: boolean;
  isRep: boolean;

  /** Which console areas this role may open; the rest show "Use the Ground Team app" (§3.1). */
  canSeeConsole: Readonly<Record<ConsoleRoute, boolean>>;
  /** Dashboard scope: the whole visible team, or only "My numbers" (reps, from `team_metrics`). */
  dashboardScope: 'team' | 'self';

  // Company configuration (admin writes; managers read; reps don't open Settings in the console).
  canEditSettings: boolean;
  canEditPipelines: boolean;
  canEditVisitResults: boolean;
  canEditCustomFields: boolean;
  canEditLeadSources: boolean;
  /** Teams editor (`/team/teams`): admin only (the `teams` collection is admin-write). */
  canEditTeams: boolean;
  canSeeIntegrations: boolean;

  // Territories, goals, templates, autoplays (admin and manager write).
  canEditTerritories: boolean;
  canEditGoals: boolean;
  canEditTemplates: boolean;
  canEditAutoplays: boolean;

  // People (§5.4).
  /** `team_roster`: members, account status, invitations (admins and managers; codes for admins only). */
  canSeeRoster: boolean;
  /** Create, edit and delete other people's profiles, change roles, suspend (admin; `users` is admin-write). */
  canManageTeam: boolean;
  canEditOtherProfiles: boolean;
  canChangeRoles: boolean;
  canSuspendMembers: boolean;
  /** `invite_member` / `revoke_invitation` (admin only on the server). */
  canInvite: boolean;
  canRevokeInvitations: boolean;
  /** The invitation code itself (team_roster hides it from managers). */
  canSeeInviteCodes: boolean;
  /** `remove_member` (admin). */
  canRemoveMembers: boolean;
  /** Tombstone a `users` item (admin; never their own, see canDeleteProfile). */
  canDeletePeople: boolean;
  /** Edit one's own profile fields (everyone; not role/isActive/teamId/managerId unless admin). */
  canEditOwnProfile: boolean;

  // Stores and their history (§5.5, §5.6). Reps get a read-only console (§3.1) even where the server would let
  // them write their own stores; they do that in the app.
  canEditRecords: boolean;
  canCreateRecords: boolean;
  /** Assign owners (single and bulk), reassign scheduled activities. */
  canAssign: boolean;
  /** Selection + bulk bar on lists, the pipeline and the map. */
  canBulkEdit: boolean;
  canChangeStage: boolean;
  canArchiveRecords: boolean;
  /** Delete permanently (tombstone with clean-up); reps can only archive, and not from the console. */
  canDeleteRecords: boolean;
  /** Log visits/calls/notes and schedule from the office. */
  canLogActivities: boolean;
  /** Delete any history entry; otherwise only one's own (server: reps tombstone own items only). */
  canDeleteAnyActivity: boolean;
  canExportCsv: boolean;
  /** Upload documents and record photos (Phase 2). */
  canUploadFiles: boolean;

  // Map (§5.7).
  /** Rectangle / lasso / click selection and its actions. */
  canSelectOnMap: boolean;
  /** Reps' last known positions layer. */
  canSeeRepLocations: boolean;
  /** Drag a pin to move a store (Phase 2, with confirm). */
  canMovePins: boolean;

  // Pipeline (§5.9).
  canDragPipeline: boolean;

  /** Routes and day logs are field data: the console only reads them, whatever the server allows. */
  canEditRoutes: boolean;
  canEditDayLogs: boolean;
}

/** Console areas per role (§5.0 table, §3.1 reduced console for reps). */
function consoleAccess(role: Role): Record<ConsoleRoute, boolean> {
  const manager = role !== 'rep';
  return {
    dashboard: true, // reps: "My numbers"
    stores: true, // reps: their own and visible stores, read-only
    followUps: true, // reps: "My follow-ups", the scheduled work assigned to them
    map: manager,
    pipeline: manager,
    territories: manager,
    team: manager,
    // The company library: every active member reads it (storage `can_read_file`, M1:354).
    documents: true,
    reports: manager,
    leaderboard: manager,
    // Admins edit; managers read (the server lets members read the configuration).
    settings: manager,
    account: true,
  };
}

/** The full permission set for a role. Pure: tests and non-React code use this directly. */
export function can(role: Role): Permissions {
  const isAdmin = role === 'admin';
  const isManager = role !== 'rep';
  const write = (c: Collection) => collectionAccess(role, c) === 'write';
  return {
    role,
    isAdmin,
    isManager,
    isRep: role === 'rep',

    canSeeConsole: consoleAccess(role),
    dashboardScope: isManager ? 'team' : 'self',

    canEditSettings: write('settings'),
    canEditPipelines: write('pipelines'),
    canEditVisitResults: write('visitResults'),
    canEditCustomFields: write('customFields'),
    canEditLeadSources: write('leadSources'),
    canEditTeams: write('teams'),
    canSeeIntegrations: collectionAccess(role, 'integrations') !== 'none',

    canEditTerritories: write('territories'),
    canEditGoals: write('goals'),
    canEditTemplates: write('templates'),
    canEditAutoplays: write('autoplays'),

    canSeeRoster: isManager,
    canManageTeam: write('users'),
    canEditOtherProfiles: write('users'),
    canChangeRoles: isAdmin,
    canSuspendMembers: isAdmin,
    canInvite: isAdmin,
    canRevokeInvitations: isAdmin,
    canSeeInviteCodes: isAdmin,
    canRemoveMembers: isAdmin,
    canDeletePeople: write('users'),
    canEditOwnProfile: true,

    canEditRecords: isManager,
    canCreateRecords: isManager,
    canAssign: isManager,
    canBulkEdit: isManager,
    canChangeStage: isManager,
    canArchiveRecords: isManager,
    canDeleteRecords: isManager && write('records'),
    canLogActivities: isManager,
    canDeleteAnyActivity: isManager && write('activities'),
    canExportCsv: isManager,
    canUploadFiles: isManager,

    canSelectOnMap: isManager,
    canSeeRepLocations: isManager,
    canMovePins: isManager,

    canDragPipeline: isManager,

    canEditRoutes: false,
    canEditDayLogs: false,
  };
}

// One frozen object per role, so `useCan()` returns a stable reference (safe in dependency arrays).
const BY_ROLE: Readonly<Record<Role, Permissions>> = {
  admin: Object.freeze(can('admin')),
  manager: Object.freeze(can('manager')),
  rep: Object.freeze(can('rep')),
};

/** Cached `can(role)`. */
export function permissionsFor(role: Role): Permissions {
  return BY_ROLE[role];
}

/** Whether `role` may open the console page at `pathname` (paths outside the console are not gated here). */
export function canSeeRoute(role: Role, pathname: string): boolean {
  const route = consoleRouteFor(pathname);
  return route === null ? true : BY_ROLE[role].canSeeConsole[route];
}

type Me = Pick<SessionCtx, 'role' | 'profileId'>;

function sameProfile(a: string | null | undefined, b: string | null | undefined): boolean {
  return !!a && !!b && a.toLowerCase() === b.toLowerCase();
}

/** Edit a profile's ordinary fields (name, phone, color…): admins any; everyone else only their own. */
export function canEditProfile(me: Me, profileId: string): boolean {
  return me.role === 'admin' || sameProfile(me.profileId, profileId);
}

/**
 * Change `role`, `isActive`, `teamId` or `managerId` of a profile: admins only (the server refuses these keys on
 * anyone's own profile unless admin). Role and active state are never changed on oneself from the console (§5.4:
 * "You cannot change your own role", "Not on yourself").
 */
export function canChangeRoleOf(me: Me, profileId: string): boolean {
  return me.role === 'admin' && !sameProfile(me.profileId, profileId);
}

export function canSuspend(me: Me, profileId: string): boolean {
  return me.role === 'admin' && !sameProfile(me.profileId, profileId);
}

/** Team and manager assignment of a profile (admin; allowed on oneself too). */
export function canAssignTeamOf(me: Me): boolean {
  return me.role === 'admin';
}

/** Tombstone a `users` item: admins, never their own profile (M1: `not (tombstone and id = me)`). */
export function canDeleteProfile(me: Me, profileId: string): boolean {
  return me.role === 'admin' && !sameProfile(me.profileId, profileId);
}

/** Delete one history entry (activity/comm/attachment): managers any; a rep only their own (`userId`/`uploadedBy`). */
export function canDeleteActivity(me: Me, authorId: string | null | undefined): boolean {
  return me.role !== 'rep' || sameProfile(me.profileId, authorId);
}

/** Permissions of the signed-in member. Only under routes that require a company (uses `useSession()`). */
export function useCan(): Permissions {
  const { role } = useSession();
  return useMemo(() => permissionsFor(role), [role]);
}
